import { openProfileDocument } from "../../../backend/transport";
import React, { useState, useEffect } from 'react';
import { useLoader } from '../../../context/LoaderContext';
import { useSearchParams } from "../../../backend/navigation";
import { SearchBar, TitleBar, DropDown, PaginatedTable } from '../../common';
import { BILLS_COLUMN, ROUTES, TITLES, BUTTON, LOCAL_STORAGE } from '../../../constants';
import { Button } from '../../shared';
import { vendorBill, printBillPackingList } from '../../../service';
import { useDebounce } from '../../../hooks';
import { billDataCompiler, getLocalStorageItem } from '../../../utils';

export const BillsContent = () => {
  const { setLoading } = useLoader();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or default values
  const [billsData, setBillsData] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [search, setSearch] = useState(false);
  const [sort1, setSort1] = useState(searchParams.get('filter') || BILLS_COLUMN[10].name);
  const [sortingArray1, setSortingArray1] = useState([]);
  const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);
  const [query, setQuery] = useState(() => ({ term: searchTerm, filter: sort1, page: pageNumber }));
  const [isLoading, setIsLoading] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  const [filteredColumn, setFilteredColumn] = useState(BILLS_COLUMN);
  const [billList, setBillList] = useState([]);
  const [selectionMode, setSelectionMode] = useState("page"); // "page" | "all"
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [globalSelectAll, setGlobalSelectAll] = useState(false);

  // Update URL params when values change
  const updateUrlParams = (params = {}) => {
    const newParams = {};

    // Only include non-empty values
    if (params.search) newParams.search = params.search;
    // Only include filter if it's not the default value
    if (params.filter && params.filter !== BILLS_COLUMN[10].name) newParams.filter = params.filter;
    if (params.page) newParams.page = params.page.toString();

    setSearchParams(newParams, { replace: true });
  };

  useEffect(() => {
    const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);
    const isFabric = vendorType === 'fabric';
    const isDyeing = vendorType === 'dyeing';
    const isClothing = vendorType === 'clothing';
    let columns = (isFabric || isDyeing || isClothing) && printMode ? BILLS_COLUMN : BILLS_COLUMN.filter((col) => col.name !== '*');
    columns = columns.filter((col) => {
      if (col.name === 'Container No' && (vendorType === 'fabric' || vendorType === 'dyeing')) {
        return false; // Hide Container No if vendor is fabric or dyeing
      }
      return true;
    });
    setFilteredColumn(columns);
  }, [printMode]);

  // A query is a submitted snapshot. Editing the input alone does not fetch.
  const fetchBills = useDebounce(({ term, filter, page }) => {
    setIsLoading(true);
    setGlobalSelectAll(false);
    setSelectionMode("page");
    const params = {
      page_number: page,
      search: term ? {
        draft_number: term,
        invoice_origin: term,
        'invoice_line_ids.future_lot': term,
        ref: term,
        container_no: term,
        invoice_number: term,
      } : {},
      sort: { selection: filter !== BILLS_COLUMN[10].name ? { state: filter.toLowerCase() } : {} },
    };
    updateUrlParams({ search: term, filter, page });
    vendorBill({ data: params })
      .then((response) => {
        setLoading(false);
        if (response?.data?.records?.length > 0) {
          setBillsData(response.data.records);
          setPaginationData(response.data.params);

          // Always update the sorting array with the latest data from the API
          const name = BILLS_COLUMN[10].dataField[0];
          if (response?.data[name] && Array.isArray(response?.data[name])) {
            setSortingArray1(response.data[name]);
          }
        } else {
          setBillsData([]);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, 400);

  const getBillsList = ({ page_number = pageNumber, filter = sort1 } = {}) => {
    setQuery({ term: searchTerm.trim(), filter, page: page_number });
  };

  useEffect(() => {
    fetchBills(query);
  }, [fetchBills, query]);

  const handleSearch = (nextSearch) => {
    setSearch(nextSearch);
    if (nextSearch) {
      setPageNumber(1);
      getBillsList({ page_number: 1 });
    }
  };

  const handlePageChange = (nextPage) => {
    const page = typeof nextPage === 'function' ? nextPage(pageNumber) : nextPage;
    if (!page || page === pageNumber) return;
    setPageNumber(page);
    getBillsList({ page_number: page });
  };

  const handleSortChange = (filter) => {
    setSort1(filter);
    setPageNumber(1);
    getBillsList({ page_number: 1, filter });
  };

  const resetFilter = () => handleSortChange(BILLS_COLUMN[10].name);
  const clearSearch = () => {
    setPageNumber(1);
    setSearch(false);
    setQuery({ term: '', filter: sort1, page: 1 });
  };

  const handleTogglePrint = () => {
    if (printMode) { setPrintMode(0); setBillList([]); setSelectionMode('page'); setGlobalSelectAll(false); }
    else setPrintMode(1);
  }

  const handlePrintPackingList = async () => {
    try {
      let payload;
      if (selectionMode === "all") {
        const sortSelection = {};
        if (sort1 !== BILLS_COLUMN[10].name) {
          sortSelection.state = sort1.toLowerCase();
        }
        payload = {
          select_all: true,
          search: searchTerm
            ? {
              name: searchTerm,
              invoice_origin: searchTerm,
              'invoice_line_ids.future_lot': searchTerm,
              ref: searchTerm,
              container_no: searchTerm,
              invoice_number: searchTerm,
            }
            : {},
          sort: {
            selection: Object.keys(sortSelection).length > 0 ? sortSelection : {},
          },
          excluded_ids: Array.from(excludedIds),
        };
      } else {
        payload = {
          select_all: false,
          bill_ids: billList.map((bi) => bi.id),
        };
      }

      const response = await printBillPackingList({ data: payload });

      if (response?.data) {
        openProfileDocument(response.data, "_blank");
        setBillList([]);
        setExcludedIds(new Set());
        setSelectionMode("page");
        setPrintMode(false);
        setGlobalSelectAll(false);
      } else {
        console.error("Server Error or Invalid Response: ", response);
      }
    } catch (error) {
      console.error("Server Error: ", error);
    }
  };

  const addBillToList = ({ id }) => {
    if (selectionMode === "all") {
      const next = new Set(excludedIds);
      if (excludedIds.has(id)) {
        next.delete(id); // re-check
      } else {
        next.add(id);    // uncheck
      }
      setExcludedIds(next);
    } else {
      if (billList.some((bi) => bi.id === id)) {
        setBillList((prev) => prev.filter((bi) => bi.id !== id));
      } else {
        setBillList((prev) => [{ id }, ...prev]);
      }
    }
  };

  const checked = (id) => {
    if (selectionMode === "all") {
      return !excludedIds.has(id);
    }
    return billList.some((bi) => bi.id === id);
  };

  const handleSelectAllVisible = () => {
    const visibleAssignedIds = billsData.map(row => row.id);
    visibleAssignedIds.forEach(id => {
      if (!checked(id)) {
        addBillToList({ id });
      }
    });
    setSelectionMode("page");
    setExcludedIds(new Set());
  };

  const handleSelectAllMatching = () => {
    setSelectionMode("all");
    setExcludedIds(new Set());
    setGlobalSelectAll(true);
    setBillList([]);
  };

  return (
    <>
      <TitleBar title={[{ text: TITLES.BILLS, route: ROUTES.BILLS }]} />

      {/* Search and Filters */}
      <div className="d-flex align-items-center justify-content-start gap-3 w-100">
        <SearchBar
          search={search}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          setSearch={handleSearch}
          onDefaultSearch={clearSearch}
        />
        <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
          <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={handleSortChange} />
          {sort1 !== BILLS_COLUMN[10].name && (
            <Button
              clickHandler={resetFilter}
              className="blue-btn d-flex align-items-center justify-content-center px-3"
            >
              {BUTTON.CLEAR_FILTERS}
            </Button>
          )}
        </div>
      </div>

      {/* Records Selected + Print Button */}
      <div className="d-flex justify-content-between align-items-center mb-3 w-100">
        {/* Left: Buttons + Selected count */}
        <div className="d-flex align-items-center gap-3">
          {(getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'fabric' || getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'dyeing' || getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'clothing') && (
            <>
              <Button
                clickHandler={handleTogglePrint}
                className="blue-btn d-flex align-items-center justify-content-center px-3"
              >
                {printMode ? BUTTON.CANCEL : BUTTON.PRINT_BILL}
              </Button>

              {(printMode && !globalSelectAll) ? (
                <>
                  <Button
                    clickHandler={handleSelectAllVisible}
                    className="blue-btn d-flex align-items-center justify-content-center px-3"
                  >
                    Select Page
                  </Button>
                </>
              ) : ''}
            </>
          )}

          {(billList.length > 0 && !globalSelectAll) && (
            <>
              <span className="text-muted ms-2">
                {billList.length} {billList.length === 1 ? 'record' : 'records'} selected
              </span>
              {!(billList.length === paginationData?.total_records) && <span
                onClick={handleSelectAllMatching}
                className="text-primary cursor-pointer fw-semibold"
                role="button"
              >
                Select All ({paginationData?.total_records})
              </span>
              }
            </>
          )}
          {selectionMode === 'all' && (
            <>
              <span className="text-muted ms-2">
                {paginationData?.total_records - excludedIds.size}{' '}
                {(paginationData?.total_records - excludedIds.size) === 1 ? 'record' : 'records'} selected
              </span>
              {!(billList.length === paginationData?.total_records) && excludedIds.size > 0 && <span
                onClick={handleSelectAllMatching}
                className="text-primary cursor-pointer fw-semibold"
                role="button"
              >
                Select All ({paginationData?.total_records})
              </span>}
            </>
          )}
        </div>

        {/* Right: Print button */}
        <div className="d-flex">
          {(getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'fabric' || getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'dyeing' || getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'clothing') &&
            (billList.length > 0 || selectionMode === 'all') && (
              <Button
                clickHandler={handlePrintPackingList}
                className="blue-btn d-flex align-items-center justify-content-center px-3"
              >
                {BUTTON.PRINT}
              </Button>
            )}
        </div>
      </div>


      {/* Table */}
      {Array.isArray(billsData) ? (
        <PaginatedTable
          columns={filteredColumn}
          initialData={billsData}
          colFilter={false}
          to={printMode ? '' : ROUTES.BILLS}
          compiler={{ use: true, tool: billDataCompiler }}
          func={{ addBillToList, checked }}
          pageNumber={pageNumber}
          setPageNumber={handlePageChange}
          paginationData={paginationData}
          isLoading={isLoading}
        />
      ) : (
        <div>
          <h4 className="text-center px-1 py-5">No Records Found</h4>
        </div>
      )}
    </>
  );
};
